import { useSyncExternalStore } from 'react';
import { tg } from './telegram';

export const LANGS = ['ru', 'en'] as const;
export type Lang = (typeof LANGS)[number];

export const LANG_LABEL: Record<Lang, string> = { ru: 'RU', en: 'EN' };

const KEY = 'gruzbot_lang';

/**
 * Словари интерфейса.
 *
 * Переведена оболочка: навигация, шапка, действия и подписи, которые
 * повторяются на всех экранах. Тексты внутри страниц добавляются сюда по мере
 * правок — переводить их пачкой без нужды значит завести второй словарь,
 * который сразу разойдётся с первым.
 *
 * Ключ — русская строка: так непереведённое место видно сразу и читается,
 * а не превращается в «screen.feed.title».
 */
const EN: Record<string, string> = {
  // Навигация
  Грузы: 'Loads',
  Заявка: 'Post',
  Отклики: 'Bids',
  Машины: 'Trucks',
  Рейсы: 'Trips',
  Компания: 'Company',
  Профиль: 'Profile',
  Админка: 'Admin',
  Меню: 'Menu',
  Финансы: 'Finance',
  Уведомления: 'Notifications',
  'Биржа грузоперевозок': 'Freight exchange',

  // Заголовки экранов
  Груз: 'Load',
  'Новая заявка': 'New load',
  'Мои заявки': 'My loads',
  'Свободные машины': 'Available trucks',
  Администрирование: 'Administration',

  // Действия
  Сохранить: 'Save',
  Отмена: 'Cancel',
  Удалить: 'Delete',
  Добавить: 'Add',
  Выйти: 'Sign out',
  Назад: 'Back',
  Откликнуться: 'Place bid',
  Прикрепить: 'Attach',
  Загрузить: 'Upload',
  Поддержка: 'Support',

  // Состояния
  'Загружаю…': 'Loading…',
  'Ничего не найдено': 'Nothing found',
  Онлайн: 'Online',
  Подключаюсь: 'Connecting',
  'Нет связи': 'Offline',

  // Тема и язык
  Тема: 'Theme',
  Светлая: 'Light',
  Тёмная: 'Dark',
  Системная: 'System',
  Язык: 'Language',

  // Роли
  Логист: 'Shipper',
  Перевозчик: 'Carrier',
  Водитель: 'Driver',
  Диспетчер: 'Dispatcher',
  Владелец: 'Owner',
  Администратор: 'Administrator',
};

const DICT: Record<Lang, Record<string, string>> = { ru: {}, en: EN };

function detect(): Lang {
  const saved = localStorage.getItem(KEY);
  if (saved === 'ru' || saved === 'en') return saved;

  // Язык клиента Telegram точнее, чем язык браузера: Mini App открывают
  // прямо из мессенджера, и настройка там уже сделана.
  const code = tg?.initDataUnsafe?.user?.language_code ?? navigator.language;
  return code?.startsWith('ru') ? 'ru' : 'en';
}

let current: Lang = detect();
const listeners = new Set<() => void>();

function subscribe(fn: () => void) {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

export function setLang(lang: Lang) {
  if (lang === current) return;
  current = lang;
  localStorage.setItem(KEY, lang);
  document.documentElement.lang = lang;
  for (const fn of listeners) fn();
}

export function getLang(): Lang {
  return current;
}

/** Перевод строки. Нет в словаре — возвращаем как есть. */
export function t(text: string): string {
  return DICT[current][text] ?? text;
}

/**
 * Хук перевода.
 *
 * Через useSyncExternalStore, а не через контекст: язык меняется редко,
 * а оборачивать всё дерево провайдером ради этого — лишний слой.
 */
export function useT() {
  const lang = useSyncExternalStore(subscribe, getLang, getLang);

  return {
    lang,
    t,
    setLang,
    /** Следующий язык по кругу — для кнопки-переключателя. */
    next: () => LANGS[(LANGS.indexOf(lang) + 1) % LANGS.length]!,
  };
}
